import { useEffect, useRef, useState } from 'react';
import { API_BASE, setupAPI } from '../../api/client';
import type { QREvent } from '../../types';
import { QRCountdown } from './QRCountdown';

type QRState = 'loading' | 'showing' | 'connected' | 'timeout' | 'error';

interface ManagedWhatsAppStepProps {
  whatsappConnected: boolean;
  onConnected: () => void;
  onComplete: () => void;
  onSkip: () => void;
}

export function ManagedWhatsAppStep({
  whatsappConnected,
  onConnected,
  onComplete,
  onSkip,
}: ManagedWhatsAppStepProps) {
  const [qrState, setQrState] = useState<QRState>(
    whatsappConnected ? 'connected' : 'loading'
  );
  const [qrData, setQrData] = useState<QREvent | null>(null);
  const [restarting, setRestarting] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  function trackedTimeout(fn: () => void, ms: number) {
    const id = setTimeout(fn, ms);
    timeoutsRef.current.push(id);
    return id;
  }

  useEffect(() => {
    if (whatsappConnected) {
      setQrState('connected');
      return;
    }

    function connectSSE() {
      const es = new EventSource(`${API_BASE}/api/setup/qr-stream`);
      esRef.current = es;

      es.addEventListener('qr', (e: MessageEvent) => {
        try {
          const data: QREvent = JSON.parse(e.data);
          setQrData(data);
          setQrState('showing');
        } catch {
          setQrData({ image: e.data, ttl: 20, index: 1, total: 6 });
          setQrState('showing');
        }
      });

      es.addEventListener('connected', () => {
        es.close();
        setQrState('connected');
        onConnected();
        trackedTimeout(onComplete, 800);
      });

      es.addEventListener('qr_timeout', () => {
        es.close();
        setQrState('timeout');
      });

      es.addEventListener('ping', () => {});

      es.onerror = () => {
        es.close();
        setQrState('error');
        trackedTimeout(connectSSE, 3000);
      };
    }

    connectSSE();

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [whatsappConnected, onConnected, onComplete]);

  async function handleRestart() {
    setRestarting(true);
    setQrState('loading');
    setQrData(null);
    try {
      await setupAPI.restartQR();
    } catch {
      // continue anyway
    }
    trackedTimeout(() => {
      setRestarting(false);
      if (esRef.current) esRef.current.close();
      const es = new EventSource(`${API_BASE}/api/setup/qr-stream`);
      esRef.current = es;

      es.addEventListener('qr', (e: MessageEvent) => {
        try {
          const data: QREvent = JSON.parse(e.data);
          setQrData(data);
          setQrState('showing');
        } catch {
          setQrData({ image: e.data, ttl: 20, index: 1, total: 6 });
          setQrState('showing');
        }
      });
      es.addEventListener('connected', () => {
        es.close();
        setQrState('connected');
        onConnected();
        trackedTimeout(onComplete, 800);
      });
      es.addEventListener('qr_timeout', () => {
        es.close();
        setQrState('timeout');
      });
      es.addEventListener('ping', () => {});
      es.onerror = () => {
        es.close();
        setQrState('error');
      };
    }, 2000);
  }

  // Already connected
  if (qrState === 'connected' || whatsappConnected) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-ink mb-2" data-ph-no-mask>Connect WhatsApp</h1>
        <div className="flex flex-col items-center py-8">
          <div className="w-16 h-16 rounded-full bg-success-bg flex items-center justify-center mb-3">
            <span className="material-icons-round text-success text-3xl">check_circle</span>
          </div>
          <p className="text-lg font-semibold text-success" data-ph-no-mask>Connected</p>
          <p className="text-sm text-ink-secondary mt-1" data-ph-no-mask>Your WhatsApp is linked and ready.</p>
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={onComplete}
            className="px-5 py-2.5 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-deep transition-colors"
            data-ph-no-mask
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink mb-1" data-ph-no-mask>Connect WhatsApp</h1>
      <p className="text-sm text-ink-secondary mb-6 leading-relaxed" data-ph-no-mask>
        Open WhatsApp on your phone, go to{' '}
        <strong className="text-ink">Settings &rarr; Linked Devices &rarr; Link a Device</strong>{' '}
        and scan the QR code below.
      </p>

      <div className="flex flex-col items-center">
        {qrState === 'loading' && (
          <div className="py-12 flex flex-col items-center gap-3">
            <span className="material-icons-round text-accent text-3xl animate-spin">sync</span>
            <p className="text-sm text-ink-secondary" data-ph-no-mask>
              {restarting ? 'Requesting new QR code...' : 'Generating QR code...'}
            </p>
          </div>
        )}

        {qrState === 'showing' && qrData && (
          <QRCountdown
            image={qrData.image}
            ttl={qrData.ttl}
            index={qrData.index}
            total={qrData.total}
          />
        )}

        {qrState === 'timeout' && (
          <div className="py-8 flex flex-col items-center gap-3 text-center">
            <div className="w-14 h-14 rounded-full bg-canvas border border-edge flex items-center justify-center">
              <span className="material-icons-round text-ink-tertiary text-2xl">qr_code</span>
            </div>
            <p className="text-sm font-medium text-ink" data-ph-no-mask>QR code expired</p>
          </div>
        )}

        {qrState === 'error' && (
          <div className="py-8 flex flex-col items-center gap-3 text-center">
            <span className="material-icons-round text-warning text-3xl">wifi_off</span>
            <p className="text-sm text-ink-secondary" data-ph-no-mask>Connection lost. Retrying...</p>
          </div>
        )}
      </div>

      {qrState === 'showing' && (
        <p className="text-xs text-ink-tertiary text-center mt-4" data-ph-no-mask>
          Waiting for scan...
        </p>
      )}

      <div className="flex justify-center mt-3">
        <button
          onClick={() => void handleRestart()}
          disabled={restarting}
          className="flex items-center gap-1.5 text-xs text-ink-tertiary hover:text-accent transition-colors"
          data-ph-no-mask
        >
          <span className="material-icons-round text-sm">refresh</span>
          {restarting ? 'Refreshing...' : 'Get new QR code'}
        </button>
      </div>

      <div className="flex justify-center mt-6">
        <button
          onClick={onSkip}
          className="text-sm text-ink-tertiary hover:text-ink-secondary transition-colors"
          data-ph-no-mask
        >
          I&apos;ll do this later
        </button>
      </div>
    </div>
  );
}
